import {PaletteColorsEnum} from '../../../../core/src/models/api/paletteModel';
import {isPaletteColor} from '../../../../core/src/models/helpers/colorHelpers';
import {colorPrimitive} from '../../styles/primitives/colorPrimitive';

export type AccentColor = PaletteColorsEnum | 'inverse';

/**
 * Keys of `theme.accent`. They follow the Figma token names, so the palette values `grey` and `pink` (which are
 * persisted API values) map to `gray` and `magenta` here.
 */
export type AccentThemeKey = Exclude<`${AccentColor}`, 'grey' | 'pink'> | 'gray' | 'magenta';

export function toAccentThemeKey(color: `${AccentColor}` | AccentThemeKey): AccentThemeKey {
  if (color === 'grey') {
    return 'gray';
  }

  if (color === 'pink') {
    return 'magenta';
  }

  return color;
}

export function isAccentThemeKey(color: unknown): color is AccentThemeKey {
  return (
    color === 'gray' || color === 'magenta' || (isAccentColor(color) && toAccentThemeKey(color) === color)
  );
}
export interface AccentSemanticColor {
  'text-primary': string;
  'text-secondary': string;
  highlight: string;
  surface: string;
  surfaceHover: string;
}

export type AccentSemanticStyles = Record<AccentThemeKey, AccentSemanticColor>;
export type AccentColorVariant = keyof AccentSemanticColor;

export const lightAccentSemanticStyles: AccentSemanticStyles = {
  inverse: {
    'text-primary': colorPrimitive.gray.white,
    'text-secondary': colorPrimitive.gray['200'],
    highlight: colorPrimitive.gray.white,
    surface: colorPrimitive.alpha.black.black1500,
    surfaceHover: colorPrimitive.alpha.black.black2000,
  },
  gray: {
    'text-primary': colorPrimitive.gray['800'],
    'text-secondary': colorPrimitive.gray['500'],
    highlight: colorPrimitive.gray['800'],
    // We use alphas for grey surfaces to always make them visible on grey backgrounds.
    surface: colorPrimitive.alpha.black.black100,
    surfaceHover: colorPrimitive.alpha.black.black200,
  },
  magenta: {
    'text-primary': colorPrimitive.magenta['700'],
    'text-secondary': colorPrimitive.magenta['500'],
    highlight: colorPrimitive.magenta['600'],
    surface: colorPrimitive.magenta['200'],
    surfaceHover: colorPrimitive.magenta['300'],
  },
  [PaletteColorsEnum.RED]: {
    'text-primary': colorPrimitive.red['700'],
    'text-secondary': colorPrimitive.red['500'],
    highlight: colorPrimitive.red['600'],
    surface: colorPrimitive.red['200'],
    surfaceHover: colorPrimitive.red['300'],
  },
  [PaletteColorsEnum.ORANGE]: {
    'text-primary': colorPrimitive.orange['700'],
    'text-secondary': colorPrimitive.orange['500'],
    highlight: colorPrimitive.orange['500'],
    surface: colorPrimitive.orange['200'],
    surfaceHover: colorPrimitive.orange['300'],
  },
  [PaletteColorsEnum.YELLOW]: {
    'text-primary': colorPrimitive.yellow['700'],
    'text-secondary': colorPrimitive.yellow['400'],
    highlight: colorPrimitive.yellow['400'],
    surface: colorPrimitive.yellow['200'],
    surfaceHover: colorPrimitive.yellow['300'],
  },
  [PaletteColorsEnum.GREEN]: {
    'text-primary': colorPrimitive.green['700'],
    'text-secondary': colorPrimitive.green['500'],
    highlight: colorPrimitive.green['600'],
    surface: colorPrimitive.green['200'],
    surfaceHover: colorPrimitive.green['300'],
  },
  [PaletteColorsEnum.TEAL]: {
    'text-primary': colorPrimitive.teal['700'],
    'text-secondary': colorPrimitive.teal['500'],
    highlight: colorPrimitive.teal['600'],
    surface: colorPrimitive.teal['200'],
    surfaceHover: colorPrimitive.teal['300'],
  },
  [PaletteColorsEnum.BLUE]: {
    'text-primary': colorPrimitive.blue['700'],
    'text-secondary': colorPrimitive.blue['500'],
    highlight: colorPrimitive.blue['600'],
    surface: colorPrimitive.blue['200'],
    surfaceHover: colorPrimitive.blue['300'],
  },
  [PaletteColorsEnum.PURPLE]: {
    'text-primary': colorPrimitive.purple['700'],
    'text-secondary': colorPrimitive.purple['500'],
    highlight: colorPrimitive.purple['600'],
    surface: colorPrimitive.purple['200'],
    surfaceHover: colorPrimitive.purple['300'],
  },
};

export const redesignedLightAccentSemanticStyles: AccentSemanticStyles = {
  ...lightAccentSemanticStyles,
  [PaletteColorsEnum.BLUE]: {
    'text-primary': colorPrimitive.blurple['700'],
    'text-secondary': colorPrimitive.blurple['500'],
    highlight: colorPrimitive.blurple['600'],
    surface: colorPrimitive.blurple['200'],
    surfaceHover: colorPrimitive.blurple['300'],
  },
};

export const darkAccentSemanticStyles: AccentSemanticStyles = {
  inverse: {
    'text-primary': colorPrimitive.gray.black,
    'text-secondary': colorPrimitive.gray['800'],
    highlight: colorPrimitive.gray.black,
    surface: colorPrimitive.alpha.white.white1500,
    surfaceHover: colorPrimitive.alpha.white.white2000,
  },
  gray: {
    'text-primary': colorPrimitive.gray['400'],
    'text-secondary': colorPrimitive.gray['700'],
    highlight: colorPrimitive.gray['500'],
    // We use alphas for grey surfaces to always make them visible on grey backgrounds.
    surface: colorPrimitive.alpha.white.white200,
    surfaceHover: colorPrimitive.alpha.white.white400,
  },
  magenta: {
    'text-primary': colorPrimitive.magenta['500'],
    'text-secondary': colorPrimitive.magenta['600'],
    highlight: colorPrimitive.magenta['500'],
    surface: colorPrimitive.magenta['900'],
    surfaceHover: colorPrimitive.magenta['800'],
  },
  [PaletteColorsEnum.RED]: {
    'text-primary': colorPrimitive.red['400'],
    'text-secondary': colorPrimitive.red['500'],
    highlight: colorPrimitive.red['500'],
    surface: colorPrimitive.red['900'],
    surfaceHover: colorPrimitive.red['800'],
  },
  [PaletteColorsEnum.ORANGE]: {
    'text-primary': colorPrimitive.orange['400'],
    'text-secondary': colorPrimitive.orange['500'],
    highlight: colorPrimitive.orange['500'],
    surface: colorPrimitive.orange['900'],
    surfaceHover: colorPrimitive.orange['800'],
  },
  [PaletteColorsEnum.YELLOW]: {
    'text-primary': colorPrimitive.yellow['400'],
    'text-secondary': colorPrimitive.yellow['500'],
    highlight: colorPrimitive.yellow['400'],
    surface: colorPrimitive.yellow['900'],
    surfaceHover: colorPrimitive.yellow['800'],
  },
  [PaletteColorsEnum.GREEN]: {
    'text-primary': colorPrimitive.green['400'],
    'text-secondary': colorPrimitive.green['500'],
    highlight: colorPrimitive.green['500'],
    surface: colorPrimitive.green['900'],
    surfaceHover: colorPrimitive.green['800'],
  },
  [PaletteColorsEnum.TEAL]: {
    'text-primary': colorPrimitive.teal['400'],
    'text-secondary': colorPrimitive.teal['500'],
    highlight: colorPrimitive.teal['500'],
    surface: colorPrimitive.teal['900'],
    surfaceHover: colorPrimitive.teal['800'],
  },
  [PaletteColorsEnum.BLUE]: {
    'text-primary': colorPrimitive.blue['400'],
    'text-secondary': colorPrimitive.blue['500'],
    highlight: colorPrimitive.blue['500'],
    surface: colorPrimitive.blue['900'],
    surfaceHover: colorPrimitive.blue['800'],
  },
  [PaletteColorsEnum.PURPLE]: {
    'text-primary': colorPrimitive.purple['400'],
    'text-secondary': colorPrimitive.purple['500'],
    highlight: colorPrimitive.purple['500'],
    surface: colorPrimitive.purple['900'],
    surfaceHover: colorPrimitive.purple['800'],
  },
};

export const redesignedDarkAccentSemanticStyles: AccentSemanticStyles = {
  ...darkAccentSemanticStyles,
  [PaletteColorsEnum.BLUE]: {
    'text-primary': colorPrimitive.blurple['400'],
    'text-secondary': colorPrimitive.blurple['500'],
    highlight: colorPrimitive.blurple['500'],
    surface: colorPrimitive.blurple['900'],
    surfaceHover: colorPrimitive.blurple['800'],
  },
};

/** Shades accept either spelling, so palette colors (`grey`, `pink`) can be interpolated; they resolve to theme keys. */
export type AccentShade = `accent.${AccentColor | AccentThemeKey}.${keyof AccentSemanticColor}`;

export function isAccentColor(color: unknown): color is AccentColor {
  return isPaletteColor(color) || color === 'inverse';
}

export function isAccentShade(
  color: unknown,
  allowedVariants?: ReadonlyArray<keyof AccentSemanticColor>,
): color is AccentShade {
  if (!(typeof color === 'string' && color.startsWith('accent.'))) {
    return false;
  }
  const [, paletteColor, accentVariant] = color.split('.');

  if (!isAccentColor(paletteColor) && !isAccentThemeKey(paletteColor)) {
    return false;
  }

  if (allowedVariants) {
    return (allowedVariants as ReadonlyArray<unknown>).includes(accentVariant);
  }

  return (
    accentVariant in
    lightAccentSemanticStyles[isAccentColor(paletteColor) ? toAccentThemeKey(paletteColor) : paletteColor]
  );
}

export function resolveAccentShade(color: AccentShade) {
  const [, paletteColor, accentVariant] = color.split('.');

  return {
    paletteColor: toAccentThemeKey(paletteColor as AccentColor | AccentThemeKey),
    variant: accentVariant as keyof AccentSemanticColor,
  };
}

export type IconAccentVariant = 'highlight';
export type IconAccentShade = `accent.${AccentColor | AccentThemeKey}.${IconAccentVariant}`;
export function isIconAccentShade(color: unknown): color is IconAccentShade {
  return isAccentShade(color, ['highlight']);
}

export type TextAccentVariant = 'text-primary' | 'text-secondary';
export type TextAccentShade = `accent.${AccentColor | AccentThemeKey}.${TextAccentVariant}`;

export function isTextAccentShade(color: unknown): color is TextAccentShade {
  return isAccentShade(color, ['text-primary', 'text-secondary']);
}
